import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Alert, Typography, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

/**
 * SuperAdminRoute - Protected route that requires super_admin role
 * Redirects to dashboard with error message if user doesn't have super_admin role
 */
const SuperAdminRoute = ({ children }) => {
  // Use correct props from AuthContext: currentUser and isLoading (compatibility props)
  // OR use state.user and state.loading
  const { isAuthenticated, currentUser, isLoading, state } = useAuth();
  
  // Use currentUser (compatibility prop) or fallback to state.user
  const user = currentUser || state?.user;
  const loading = isLoading !== undefined ? isLoading : state?.loading;

  // Show loading state while checking authentication
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  
  // Check if user has super_admin role
  // Backend returns: user.roles = [{id, name, display_name, level}, ...]
  // Backend returns: user.primary_role = {id, name, display_name} or null
  const isSuperAdmin = user?.roles?.some(role => role.name === 'super_admin') || 
                       user?.primary_role?.name === 'super_admin' ||
                       user?.is_admin === true; // Fallback to old is_admin field
    
  // Show access denied if not super admin
  if (!isSuperAdmin) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        p: 3
      }}>
        <LockIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Access Denied
        </Typography>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
          You don't have permission to access this page. 
          Super Admin privileges are required.
        </Alert>
        <Button 
          variant="contained" 
          href="/dashboard"
          sx={{ textTransform: 'none' }}
        >
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  // User is super admin, render children
  return <>{children}</>;
};

export default SuperAdminRoute;
